import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Modal } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useCompanyServiceQueries } from 'services/company';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { CompanyWithOwnership } from 'types/company';
import Button from 'components/Button';
import Toast from 'components/Toast';
import RefreshButton from 'components/RefreshButton';
import Pagination from 'components/Pagination';
import CreateCompanyModal from '../CreateCompanyModal';
import ViewCompanyModal from '../ViewCompanyModal';
import EditCompanyModal from '../EditCompanyModal';
import {
  Search,
  Building2,
  Plus,
  Trash2,
  Eye,
  Pencil,
  UserPlus,
  Crown,
  LogOut,
} from 'lucide-react';
import styles from './Companies.module.scss';
import { ROUTES } from 'utils/constants';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';
import { useUserProfileStore } from 'stores/userProfileStore';

const COMPANIES_CACHE_KEY = 'companies_cache';
const CACHE_EXPIRATION_MS = 5 * 60 * 1000; // 5 minutes in milliseconds

const parsePageParam = (value: string | null): number => {
  if (!value) return 1;
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return 1;
  }
  return parsed;
};

function Companies() {
  const companyService = useCompanyServiceQueries();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCompanyId = useActiveCompanyStore((s) => s.activeCompanyId);
  const setActiveCompanyId = useActiveCompanyStore((s) => s.setActiveCompanyId);
  const refreshProfile = useUserProfileStore((s) => s.refreshProfile);
  const [companies, setCompanies] = useState<CompanyWithOwnership[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(() =>
    parsePageParam(searchParams.get('page')),
  );
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [viewingCompany, setViewingCompany] =
    useState<CompanyWithOwnership | null>(null);
  const [editingCompany, setEditingCompany] =
    useState<CompanyWithOwnership | null>(null);
  const [manageTarget, setManageTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [transferTarget, setTransferTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [transferUsers, setTransferUsers] = useState<
    { id: string; email: string; username?: string }[]
  >([]);
  const [transferUsersLoading, setTransferUsersLoading] = useState(false);
  const [transferUsersError, setTransferUsersError] = useState<string | null>(
    null,
  );
  const [selectedTransferUserId, setSelectedTransferUserId] = useState('');
  const [transferringOwnership, setTransferringOwnership] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [deletingCompany, setDeletingCompany] =
    useState<CompanyWithOwnership | null>(null);
  const [deleteCompanyConfirmation, setDeleteCompanyConfirmation] =
    useState('');
  const [isDeletingCompany, setIsDeletingCompany] = useState(false);
  const [leavingCompany, setLeavingCompany] =
    useState<CompanyWithOwnership | null>(null);
  const [leaveCompanyConfirmation, setLeaveCompanyConfirmation] = useState('');
  const [isLeavingCompany, setIsLeavingCompany] = useState(false);
  const [companyLogoUrls, setCompanyLogoUrls] = useState<
    Record<string, string | null>
  >({});
  const [logoMenuCompanyId, setLogoMenuCompanyId] = useState<string | null>(
    null,
  );
  const [logoMenuPosition, setLogoMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [logoViewerUrl, setLogoViewerUrl] = useState<string | null>(null);
  const [logoViewerCompanyName, setLogoViewerCompanyName] = useState('');
  const [pendingUploadCompanyId, setPendingUploadCompanyId] = useState<
    string | null
  >(null);
  const [uploadingLogoCompanyId, setUploadingLogoCompanyId] = useState<
    string | null
  >(null);
  const [deletingLogoCompanyId, setDeletingLogoCompanyId] = useState<
    string | null
  >(null);
  const logoFileInputRef = useRef<HTMLInputElement>(null);

  // Use localStorage hook with encryption and expiration
  const {
    data: cachedCompanies,
    setData: setCachedCompanies,
    isInitialized: cacheInitialized,
  } = useLocalStorage<CompanyWithOwnership[]>({
    key: COMPANIES_CACHE_KEY,
    expirationMs: CACHE_EXPIRATION_MS,
    encrypt: true,
  });

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    },
    [],
  );

  const resolveCompanyLogoUrls = useCallback(
    async (items: CompanyWithOwnership[]) => {
      const logoEntries = await Promise.all(
        items.map(async (company) => {
          if (!company.logoPath) {
            return [company.id, null] as const;
          }
          try {
            const result = await companyService.getCompanyLogoDownloadUrl(
              company.id,
            );
            return [company.id, result.downloadUrl ?? null] as const;
          } catch {
            return [company.id, null] as const;
          }
        }),
      );
      setCompanyLogoUrls(Object.fromEntries(logoEntries));
    },
    [companyService],
  );

  // Load companies
  const loadCompanies = useCallback(
    async (isReload = false) => {
      if (isReload) setReloading(true);
      else setLoading(true);
      setError(null);
      try {
        const data = await companyService.getAllCompanies();
        setCompanies(data);
        await resolveCompanyLogoUrls(data);
        // Save to cache
        await setCachedCompanies(data);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to load companies. Please try again.';
        setError(errorMessage);
        showToast(errorMessage, 'error');
      } finally {
        if (isReload) setReloading(false);
        else setLoading(false);
      }
    },
    [companyService, showToast, setCachedCompanies, resolveCompanyLogoUrls],
  );

  // Load cached data on mount - wait for cache to be initialized
  useEffect(() => {
    if (!cacheInitialized) return; // Wait for hook to finish checking cache

    if (cachedCompanies) {
      // Use cached data - it's still valid (hook already checked expiration)
      setCompanies(cachedCompanies);
      void resolveCompanyLogoUrls(cachedCompanies);
      setLoading(false);
    } else {
      // No cache or expired - fetch data immediately
      void loadCompanies();
    }
  }, [
    cachedCompanies,
    cacheInitialized,
    loadCompanies,
    resolveCompanyLogoUrls,
  ]);

  useEffect(() => {
    const closeLogoMenu = () => {
      setLogoMenuCompanyId(null);
      setLogoMenuPosition(null);
    };
    if (logoMenuCompanyId) {
      document.addEventListener('click', closeLogoMenu);
    }
    return () => {
      document.removeEventListener('click', closeLogoMenu);
    };
  }, [logoMenuCompanyId]);

  useEffect(() => {
    if (!logoMenuCompanyId) return;
    const closeOnViewportChange = () => {
      setLogoMenuCompanyId(null);
      setLogoMenuPosition(null);
    };
    window.addEventListener('resize', closeOnViewportChange);
    window.addEventListener('scroll', closeOnViewportChange, true);
    return () => {
      window.removeEventListener('resize', closeOnViewportChange);
      window.removeEventListener('scroll', closeOnViewportChange, true);
    };
  }, [logoMenuCompanyId]);

  // Filter companies based on search
  const filteredCompanies = useMemo(() => {
    const searchLower = searchQuery.toLowerCase();
    return companies.filter((company) => {
      const matchesSearch =
        company.name.toLowerCase().includes(searchLower) ||
        (company.description?.toLowerCase().includes(searchLower) ?? false) ||
        (company.address?.toLowerCase().includes(searchLower) ?? false) ||
        (company.tin?.toLowerCase().includes(searchLower) ?? false) ||
        (company.vatStatus?.toLowerCase().includes(searchLower) ?? false);
      return matchesSearch;
    });
  }, [companies, searchQuery]);

  const totalFilteredCompanies = filteredCompanies.length;

  const paginatedCompanies = useMemo(() => {
    if (totalFilteredCompanies === 0) {
      return [];
    }
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredCompanies.slice(startIndex, endIndex);
  }, [filteredCompanies, currentPage, pageSize, totalFilteredCompanies]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    const nextTotalPages = Math.max(
      1,
      Math.ceil(totalFilteredCompanies / pageSize),
    );
    setCurrentPage((prevPage) => Math.min(prevPage, nextTotalPages));
  }, [totalFilteredCompanies, pageSize]);

  useEffect(() => {
    const pageFromUrl = parsePageParam(searchParams.get('page'));
    setCurrentPage((prevPage) =>
      prevPage === pageFromUrl ? prevPage : pageFromUrl,
    );
  }, [searchParams]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (currentPage <= 1) {
          next.delete('page');
        } else {
          next.set('page', String(currentPage));
        }
        return next;
      },
      { replace: true },
    );
  }, [currentPage, setSearchParams]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }, []);

  const handleCreateCompanySuccess = useCallback(() => {
    // Reload companies list to show the new company
    void loadCompanies(true);
    showToast('Company created successfully!', 'success');
  }, [loadCompanies, showToast]);

  const handleManageUsers = useCallback(
    (company: { id: string; name: string }) => {
      if (company.id === activeCompanyId) {
        navigate(ROUTES.COMPANY.USERS);
        return;
      }
      setManageTarget({ id: company.id, name: company.name });
    },
    [activeCompanyId, navigate],
  );

  const confirmManageUsers = useCallback(async () => {
    if (!manageTarget) return;
    setSwitching(true);
    try {
      await setActiveCompanyId(manageTarget.id);
      setManageTarget(null);
      navigate(ROUTES.COMPANY.USERS);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to switch company';
      showToast(message, 'error');
    } finally {
      setSwitching(false);
    }
  }, [manageTarget, navigate, setActiveCompanyId, showToast]);

  const openTransferOwnershipModal = useCallback(
    async (company: { id: string; name: string }) => {
      setTransferTarget(company);
      setTransferUsers([]);
      setTransferUsersError(null);
      setSelectedTransferUserId('');
      setTransferUsersLoading(true);
      try {
        const users = await companyService.getCompanyUsers(company.id);
        const eligibleUsers = users.filter(
          (user) => user.role !== 'owner' && user.owned !== true,
        );
        setTransferUsers(
          eligibleUsers.map((user) => ({
            id: user.id,
            email: user.email,
            username: user.username,
          })),
        );
        if (eligibleUsers.length > 0) {
          setSelectedTransferUserId(eligibleUsers[0].id);
        }
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'Failed to load users for ownership transfer';
        setTransferUsersError(message);
      } finally {
        setTransferUsersLoading(false);
      }
    },
    [companyService],
  );

  const handleTransferOwnership = useCallback(async () => {
    if (!transferTarget || !selectedTransferUserId) return;
    setTransferringOwnership(true);
    try {
      await companyService.transferCompanyOwnership(
        transferTarget.id,
        selectedTransferUserId,
      );
      setTransferTarget(null);
      setTransferUsers([]);
      setSelectedTransferUserId('');
      await loadCompanies(true);
      showToast('Company ownership transferred successfully', 'success');
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to transfer company ownership';
      showToast(message, 'error');
    } finally {
      setTransferringOwnership(false);
    }
  }, [
    companyService,
    loadCompanies,
    selectedTransferUserId,
    showToast,
    transferTarget,
  ]);

  const handleUploadLogo = useCallback((companyId: string) => {
    setPendingUploadCompanyId(companyId);
    setLogoMenuCompanyId(null);
    setLogoMenuPosition(null);
    logoFileInputRef.current?.click();
  }, []);

  const handleLogoFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file || !pendingUploadCompanyId) return;
      if (!file.type.startsWith('image/')) {
        showToast('Please select an image file.', 'error');
        return;
      }
      const companyId = pendingUploadCompanyId;
      setUploadingLogoCompanyId(companyId);
      setPendingUploadCompanyId(null);
      try {
        const company = companies.find((c) => c.id === companyId);
        if (!company) {
          throw new Error('Company not found for upload.');
        }
        const { uploadUrl, path } =
          await companyService.getCompanyLogoUploadUrl(companyId, {
            filename: file.name,
            contentType: file.type || 'image/jpeg',
          });
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'image/jpeg' },
        });
        if (!uploadResponse.ok) {
          throw new Error('Upload failed.');
        }
        const saved = await companyService.setCompanyLogoPath(companyId, {
          path,
          filename: file.name,
          size: file.size,
          mimeType: file.type || 'image/jpeg',
        });
        const download =
          await companyService.getCompanyLogoDownloadUrl(companyId);
        const nextLogoUrl = download.downloadUrl ?? null;
        setCompanyLogoUrls((prev) => ({ ...prev, [companyId]: nextLogoUrl }));
        const nextCompanies = companies.map((item) =>
          item.id === companyId
            ? { ...item, logoPath: saved.logoPath ?? undefined }
            : item,
        );
        setCompanies(nextCompanies);
        void setCachedCompanies(nextCompanies);
        showToast('Company logo updated.', 'success');
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : 'Failed to upload company logo.';
        showToast(message, 'error');
      } finally {
        setUploadingLogoCompanyId(null);
      }
    },
    [
      companies,
      companyService,
      pendingUploadCompanyId,
      setCachedCompanies,
      showToast,
    ],
  );

  const handleViewLogo = useCallback(
    (company: CompanyWithOwnership) => {
      const logoUrl = companyLogoUrls[company.id];
      if (!logoUrl) return;
      setLogoMenuCompanyId(null);
      setLogoMenuPosition(null);
      setLogoViewerCompanyName(company.name);
      setLogoViewerUrl(logoUrl);
    },
    [companyLogoUrls],
  );

  const handleDeleteLogo = useCallback(
    async (company: CompanyWithOwnership) => {
      setLogoMenuCompanyId(null);
      setLogoMenuPosition(null);
      setDeletingLogoCompanyId(company.id);
      try {
        await companyService.removeCompanyLogo(company.id);
        const nextCompanies = companies.map((item) =>
          item.id === company.id ? { ...item, logoPath: undefined } : item,
        );
        setCompanies(nextCompanies);
        void setCachedCompanies(nextCompanies);
        setCompanyLogoUrls((prev) => ({ ...prev, [company.id]: null }));
        showToast('Company logo removed.', 'success');
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : 'Failed to remove company logo.';
        showToast(message, 'error');
      } finally {
        setDeletingLogoCompanyId(null);
      }
    },
    [companies, companyService, setCachedCompanies, showToast],
  );

  const handleDeleteCompany = useCallback(async () => {
    if (!deletingCompany || deleteCompanyConfirmation !== 'delete') return;

    setIsDeletingCompany(true);
    try {
      await companyService.deleteCompany(deletingCompany.id);
      setDeletingCompany(null);
      setDeleteCompanyConfirmation('');
      await loadCompanies(true);
      await refreshProfile(true);
      showToast('Company deleted successfully.', 'success');
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to delete company.';
      showToast(message, 'error');
    } finally {
      setIsDeletingCompany(false);
    }
  }, [
    companyService,
    deleteCompanyConfirmation,
    deletingCompany,
    loadCompanies,
    refreshProfile,
    showToast,
  ]);

  const handleLeaveCompany = useCallback(async () => {
    if (!leavingCompany || leaveCompanyConfirmation.trim() !== 'leave') return;

    setIsLeavingCompany(true);
    try {
      await companyService.leaveCompany(leavingCompany.id);
      setLeavingCompany(null);
      setLeaveCompanyConfirmation('');
      await loadCompanies(true);
      await refreshProfile(true);
      showToast('You have left the company.', 'success');
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to leave company.';
      showToast(message, 'error');
    } finally {
      setIsLeavingCompany(false);
    }
  }, [
    companyService,
    leaveCompanyConfirmation,
    leavingCompany,
    loadCompanies,
    refreshProfile,
    showToast,
  ]);

  return (
    <div className={styles.container}>
      <input
        ref={logoFileInputRef}
        type="file"
        accept="image/*"
        className={styles.hiddenInput}
        onChange={(event) => void handleLogoFileChange(event)}
      />
      <div className={styles.header}>
        <h1 className={styles.title}>Companies</h1>
        <div className={styles.headerButtons}>
          <RefreshButton
            onClick={() => void loadCompanies(true)}
            loading={reloading}
          />
          <Button onClick={() => setIsCreateModalOpen(true)}>
            <Plus size={16} />
            Create Company
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        <div className={styles.searchContainer}>
          <Search size={18} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search companies..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            className={styles.searchInput}
          />
        </div>
      </div>

      {/* Error State */}
      {error && companies.length === 0 && (
        <div className={styles.errorState}>
          <p>{error}</p>
          <Button onClick={() => void loadCompanies()}>Retry</Button>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredCompanies.length === 0 && !error && (
        <div className={styles.emptyState}>
          <Building2 size={48} className={styles.emptyIcon} />
          <p>
            {searchQuery
              ? 'No companies found matching your search.'
              : 'No companies found. You are not linked to any companies yet.'}
          </p>
        </div>
      )}

      {/* Companies Table */}
      {loading ? (
        <div className={styles.loadingState}>
          <div className={styles.tableSkeleton}>
            <div className={styles.tableSkeletonHeader}>
              {[
                'Name',
                'Description',
                'Ownership',
                'Created At',
                'Actions',
              ].map((label) => (
                <div key={label} className={styles.tableSkeletonHeaderCell}>
                  <span
                    className={`${styles.skeletonLine} ${styles.skeletonMedium}`}
                  />
                </div>
              ))}
            </div>
            <div className={styles.tableSkeletonBody}>
              {Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={`company-skeleton-${index}`}
                  className={styles.skeletonRow}
                >
                  <span
                    className={`${styles.skeletonLine} ${styles.skeletonLong}`}
                  />
                  <span
                    className={`${styles.skeletonLine} ${styles.skeletonLong}`}
                  />
                  <span
                    className={`${styles.skeletonLine} ${styles.skeletonMedium}`}
                  />
                  <span
                    className={`${styles.skeletonLine} ${styles.skeletonShort}`}
                  />
                  <span
                    className={`${styles.skeletonLine} ${styles.skeletonShort}`}
                  />
                  <span
                    className={`${styles.skeletonLine} ${styles.skeletonShort}`}
                  />
                  <span
                    className={`${styles.skeletonLine} ${styles.skeletonMedium}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.tableContainer}>
          {reloading && (
            <div className={styles.loadingOverlay}>
              <div className={styles.loadingSpinner}>Loading...</div>
            </div>
          )}
          {filteredCompanies.length > 0 && (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Ownership</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedCompanies.map((company) => (
                  <tr key={company.id}>
                    <td className={styles.nameCell}>
                      <div className={styles.nameWithLogo}>
                        <div className={styles.logoWrapper}>
                          <button
                            type="button"
                            className={styles.logoButton}
                            aria-label={`${company.name} logo options`}
                            onClick={(event) => {
                              event.stopPropagation();
                              const target = event.currentTarget;
                              const rect = target.getBoundingClientRect();
                              const menuWidth = 50;
                              const menuGap = 0;
                              const menuLeft = Math.min(
                                Math.max(8, rect.right + menuGap),
                                window.innerWidth - menuWidth - 8,
                              );
                              const menuTop = Math.min(
                                rect.bottom + menuGap,
                                window.innerHeight - 8,
                              );
                              setLogoMenuCompanyId((prev) => {
                                if (prev === company.id) {
                                  setLogoMenuPosition(null);
                                  return null;
                                }
                                setLogoMenuPosition({
                                  top: menuTop,
                                  left: menuLeft,
                                });
                                return company.id;
                              });
                            }}
                          >
                            {companyLogoUrls[company.id] ? (
                              <img
                                src={companyLogoUrls[company.id] ?? undefined}
                                alt={`${company.name} logo`}
                                className={styles.logoImage}
                              />
                            ) : (
                              <span className={styles.logoFallback}>Logo</span>
                            )}
                          </button>
                        </div>
                        <span>{company.name}</span>
                      </div>
                    </td>
                    <td className={styles.descriptionCell}>
                      {company.description || '-'}
                    </td>
                    <td className={styles.ownershipCell}>
                      {company.owned ? (
                        <span className={styles.ownerBadge}>Owner</span>
                      ) : (
                        <span className={styles.memberBadge}>Member</span>
                      )}
                    </td>
                    <td className={styles.dateCell}>
                      {formatDate(company.createdAt)}
                    </td>
                    <td className={styles.actionsCell}>
                      <div className={styles.actionsButtons}>
                        {!company.owned && (
                          <button
                            type="button"
                            className={`${styles.actionButton} ${styles.leaveActionButton}`}
                            aria-label="Leave company"
                            title="Leave company"
                            onClick={() => setLeavingCompany(company)}
                          >
                            <LogOut size={16} />
                          </button>
                        )}
                        {company.owned && (
                          <>
                            <button
                              type="button"
                              className={`${styles.actionButton} ${styles.deleteActionButton}`}
                              aria-label="Delete company"
                              title="Delete company"
                              onClick={() => {
                                setDeletingCompany(company);
                                setDeleteCompanyConfirmation('');
                              }}
                            >
                              <Trash2 size={16} />
                            </button>
                            <button
                              type="button"
                              className={styles.actionButton}
                              aria-label="View company"
                              title="View company"
                              onClick={() => setViewingCompany(company)}
                            >
                              <Eye size={16} />
                            </button>
                            <button
                              type="button"
                              className={styles.actionButton}
                              aria-label="Edit company"
                              title="Edit company"
                              onClick={() => setEditingCompany(company)}
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              type="button"
                              className={styles.actionButton}
                              aria-label="Manage users"
                              title="Manage users"
                              onClick={() => handleManageUsers(company)}
                            >
                              <UserPlus size={16} />
                            </button>
                            <button
                              type="button"
                              className={styles.actionButton}
                              aria-label="Transfer ownership"
                              title="Transfer ownership"
                              onClick={() =>
                                void openTransferOwnershipModal(company)
                              }
                            >
                              <Crown size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalFilteredCompanies > 0 && (
        <div className={styles.paginationWrapper}>
          <Pagination
            currentPage={currentPage}
            totalItems={totalFilteredCompanies}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      )}

      {/* Create Company Modal */}
      <CreateCompanyModal
        show={isCreateModalOpen}
        onHide={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateCompanySuccess}
        onError={(message) => showToast(message, 'error')}
      />
      <ViewCompanyModal
        show={!!viewingCompany}
        onHide={() => setViewingCompany(null)}
        company={viewingCompany}
      />
      <EditCompanyModal
        show={!!editingCompany}
        onHide={() => setEditingCompany(null)}
        company={editingCompany}
        onSuccess={(updated) => {
          const next = companies.map((c) =>
            c.id === updated.id ? updated : c,
          );
          setCompanies(next);
          void setCachedCompanies(next);
          setEditingCompany(null);
          showToast('Company updated successfully', 'success');
        }}
        onError={(message) => showToast(message, 'error')}
      />
      {logoViewerUrl && (
        <div
          className={styles.viewerOverlay}
          role="dialog"
          aria-modal="true"
          aria-label={`${logoViewerCompanyName} logo`}
          onClick={() => setLogoViewerUrl(null)}
        >
          <div
            className={styles.viewerTopBar}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className={styles.viewerClose}
              onClick={() => setLogoViewerUrl(null)}
            >
              Close
            </button>
          </div>
          <div className={styles.viewerImageWrap}>
            <img
              src={logoViewerUrl}
              alt={`${logoViewerCompanyName} full logo`}
              className={styles.viewerImage}
              onClick={(event) => event.stopPropagation()}
            />
          </div>
        </div>
      )}
      {logoMenuCompanyId &&
        logoMenuPosition &&
        createPortal(
          <div
            className={styles.logoMenu}
            style={{
              top: `${logoMenuPosition.top}px`,
              left: `${logoMenuPosition.left}px`,
            }}
            onClick={(event) => event.stopPropagation()}
          >
            {(() => {
              const company = companies.find((c) => c.id === logoMenuCompanyId);
              if (!company) return null;
              return (
                <>
                  <button
                    type="button"
                    className={styles.logoMenuItem}
                    onClick={() => handleViewLogo(company)}
                    disabled={!companyLogoUrls[company.id]}
                  >
                    View logo
                  </button>
                  <button
                    type="button"
                    className={styles.logoMenuItem}
                    onClick={() => handleUploadLogo(company.id)}
                    disabled={uploadingLogoCompanyId === company.id}
                  >
                    {uploadingLogoCompanyId === company.id
                      ? 'Uploading...'
                      : 'Upload logo'}
                  </button>
                  <button
                    type="button"
                    className={styles.logoMenuItem}
                    onClick={() => void handleDeleteLogo(company)}
                    disabled={
                      !companyLogoUrls[company.id] ||
                      deletingLogoCompanyId === company.id
                    }
                  >
                    {deletingLogoCompanyId === company.id
                      ? 'Deleting...'
                      : 'Delete logo'}
                  </button>
                </>
              );
            })()}
          </div>,
          document.body,
        )}

      {/* Manage Users Confirm Modal */}
      <Modal
        show={!!manageTarget}
        onHide={() => setManageTarget(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Switch Active Company</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {manageTarget ? (
            <p>
              You are about to switch active company to{' '}
              <strong>{manageTarget.name}</strong> and manage its users.
              Proceed?
            </p>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setManageTarget(null)}
            disabled={switching}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void confirmManageUsers()}
            disabled={switching}
          >
            {switching ? 'Switching...' : 'Continue'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Transfer Ownership Modal */}
      <Modal
        show={!!transferTarget}
        onHide={() => {
          if (transferringOwnership) return;
          setTransferTarget(null);
          setTransferUsers([]);
          setTransferUsersError(null);
          setSelectedTransferUserId('');
        }}
        centered
      >
        <Modal.Header closeButton={!transferringOwnership}>
          <Modal.Title>Transfer Ownership</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {transferTarget ? (
            <>
              <p>
                Transfer ownership of <strong>{transferTarget.name}</strong> to
                a linked member.
              </p>
              {transferUsersLoading ? (
                <p>Loading members...</p>
              ) : transferUsersError ? (
                <p>{transferUsersError}</p>
              ) : transferUsers.length === 0 ? (
                <p>
                  No eligible members found. Link a user first before
                  transferring ownership.
                </p>
              ) : (
                <select
                  className="form-select"
                  value={selectedTransferUserId}
                  onChange={(e) => setSelectedTransferUserId(e.target.value)}
                  disabled={transferringOwnership}
                >
                  {transferUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.username
                        ? `${user.username} (${user.email})`
                        : user.email}
                    </option>
                  ))}
                </select>
              )}
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setTransferTarget(null);
              setTransferUsers([]);
              setTransferUsersError(null);
              setSelectedTransferUserId('');
            }}
            disabled={transferringOwnership}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleTransferOwnership()}
            disabled={
              transferringOwnership ||
              transferUsersLoading ||
              !selectedTransferUserId ||
              transferUsers.length === 0
            }
          >
            {transferringOwnership ? 'Transferring...' : 'Transfer'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Company Modal */}
      <Modal
        show={!!deletingCompany}
        onHide={() => {
          if (isDeletingCompany) return;
          setDeletingCompany(null);
          setDeleteCompanyConfirmation('');
        }}
        centered
      >
        <Modal.Header closeButton={!isDeletingCompany}>
          <Modal.Title>Delete Company</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deletingCompany ? (
            <>
              <p>
                This will permanently delete{' '}
                <strong>{deletingCompany.name}</strong>. This action cannot be
                undone.
              </p>
              <p className="mb-2">
                Type <strong>delete</strong> to confirm:
              </p>
              <input
                type="text"
                className="form-control"
                value={deleteCompanyConfirmation}
                onChange={(event) =>
                  setDeleteCompanyConfirmation(event.target.value)
                }
                placeholder="delete"
                autoComplete="off"
                disabled={isDeletingCompany}
              />
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setDeletingCompany(null);
              setDeleteCompanyConfirmation('');
            }}
            disabled={isDeletingCompany}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => void handleDeleteCompany()}
            disabled={
              isDeletingCompany || deleteCompanyConfirmation.trim() !== 'delete'
            }
          >
            {isDeletingCompany ? 'Deleting...' : 'Delete Company'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Leave Company Modal */}
      <Modal
        show={!!leavingCompany}
        onHide={() => {
          if (isLeavingCompany) return;
          setLeavingCompany(null);
          setLeaveCompanyConfirmation('');
        }}
        centered
      >
        <Modal.Header closeButton={!isLeavingCompany}>
          <Modal.Title>Leave Company</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {leavingCompany ? (
            <>
              <p>
                You are about to leave <strong>{leavingCompany.name}</strong>.
                This only removes your access and does not delete the company.
              </p>
              <p className="mb-2">
                Type <strong>leave</strong> to confirm:
              </p>
              <input
                type="text"
                className="form-control"
                value={leaveCompanyConfirmation}
                onChange={(event) =>
                  setLeaveCompanyConfirmation(event.target.value)
                }
                placeholder="leave"
                autoComplete="off"
                disabled={isLeavingCompany}
              />
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setLeavingCompany(null);
              setLeaveCompanyConfirmation('');
            }}
            disabled={isLeavingCompany}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => void handleLeaveCompany()}
            disabled={
              isLeavingCompany || leaveCompanyConfirmation.trim() !== 'leave'
            }
          >
            {isLeavingCompany ? 'Leaving...' : 'Leave Company'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => {
            setToast(null);
          }}
        />
      )}
    </div>
  );
}

export default Companies;
